import { BookingsConfig, UserProfile } from 'fitbeat.gym-core';
import { BookingState, CardioOrResistance, GymTypes } from 'fitbeat.models';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { translate } from 'react-i18next';
import { JoinOrCheckinWorkoutButton } from '../../ui';
import { checkinToStudioWorkout, joinWorkout, openBookingSettings, openBagContactStep } from '../../Bookings/actions';
import { PaywallPage } from '../../auth';
import { openPaywallModalAction } from '../../payments/redux/payments-actions';
import { GymResolver } from '../../services';
import { View } from 'react-native';

class WorkoutButtonWithCountdownAndActions extends Component {
  state = {
    hasPreWorkoutStarted: false,
    hasFirstExerciseStarted: false,
    shouldShowCheckin: false,
    setIntervalId: null,
    timeRemaining: '',
    timeRemainingMilliseconds: 0,
    bookingIsForHome: false,
    isWorkoutStartingInOneHour: false,
    isCheckedIn: false,
    pendingInviteAccept: false,
    enableStudioAppCheckIn: false,
  };

  componentDidMount() {
    const { t } = this.props;

    moment.updateLocale('en', {
      relativeTime: {
        future: t('future'),
        s: function(num, withoutSuffix, key, isFuture) {
          return num > 1 ? `${num} ${t('seconds')}` : `${num} ${t('second')}`;
        },
        m: t('minute'),
        mm: `%d ${t('minutes')}`,
        h: t('anHour'),
        hh: `%d ${t('hours')}`,
      },
    });

    const hasPreWorkoutStarted = this.getWorkoutHasStarted();
    const hasFirstExerciseStarted = this.getFirstExerciseHasStarted();
    const shouldShowCheckin = this.shouldShowCheckinButton();
    if (!hasFirstExerciseStarted || !hasPreWorkoutStarted || !shouldShowCheckin) {
      this.createInterval();
    }

    const gymForBooking = GymResolver.getByName(this.props.booking.gymName);
    const bookingIsForHome = gymForBooking.type === GymTypes.Home;
    const enableStudioAppCheckIn = gymForBooking.enableStudioAppCheckIn;
    const pendingInviteAccept = this.props.booking.bookingState === BookingState.BookingInvite;
    const isCheckedIn = this.props.booking.checkedIn;

    const isWorkoutStartingInOneHour = !moment(this.props.booking.startTime)
      .tz(this.props.gymTimezone)
      .diff(moment(new Date()).tz(this.props.gymTimezone), 'hour');

    this.setState({
      hasPreWorkoutStarted,
      hasFirstExerciseStarted,
      shouldShowCheckin,
      bookingIsForHome,
      isWorkoutStartingInOneHour,
      isCheckedIn,
      pendingInviteAccept,
      enableStudioAppCheckIn,
    });
  }

  componentWillUnmount = () => {
    this.clearInterval();
  }

  componentDidUpdate = (prevProps, prevState) => {
    const prevHasFirstExerciseStarted = prevState.hasFirstExerciseStarted;
    if (prevHasFirstExerciseStarted !== this.getFirstExerciseHasStarted()) {
      this.clearInterval();
    }

    if (prevProps.booking.startTime !== this.props.booking.startTime) {
      this.createInterval();
    }

    if (prevProps.booking.checkedIn !== this.props.booking.checkedIn) {
      const isCheckedIn = this.props.booking.checkedIn;
      this.setState({
        isCheckedIn,
      });
      this.createInterval();
    }

    if (prevProps.booking.bookingState !== this.props.booking.bookingState) {
      const pendingInviteAccept = this.props.booking.bookingState === BookingState.BookingInvite;
      this.setState({
        pendingInviteAccept,
      });
      this.createInterval();
    }

    if (prevState.shouldShowCheckin !== this.shouldShowCheckinButton()) {
      this.setState({
        shouldShowCheckin: !prevState.shouldShowCheckin,
      });
      this.createInterval();
    }

    if (prevState.hasPreWorkoutStarted !== this.getWorkoutHasStarted()) {
      this.setState({
        hasPreWorkoutStarted: !prevState.hasPreWorkoutStarted,
      });
      this.createInterval();
    }
  }

  clearInterval = () => {
    const { setIntervalId } = this.state;
    if (setIntervalId) {
      clearInterval(setIntervalId);
    }
  }

  createInterval = () => {
    this.clearInterval();

    const { booking, gymTimezone } = this.props;
    const setTimeRemaining = () => {
      const timeRemaining = moment(booking.startTimeOfFirstExercise).tz(gymTimezone).toNow(true).toUpperCase();
      const timeRemainingMilliseconds = -moment().diff(moment(booking.startTimeOfFirstExercise));
      this.setState({ timeRemaining, timeRemainingMilliseconds });
    };
    setTimeRemaining();
    const setIntervalId = setInterval(setTimeRemaining, 1000);
    this.setState({ setIntervalId });
  }

  getFirstExerciseHasStarted = () =>
    moment(new Date()).tz(this.props.gymTimezone).isAfter(this.props.booking.startTimeOfFirstExercise)

  getWorkoutHasStarted = () =>
    moment(new Date()).tz(this.props.gymTimezone).isAfter(this.props.booking.startTime)

  openHomeWorkout = () => {
    const { navigation, booking, userProfile, workoutSessionService, openPaywallModal } = this.props;
    if (!UserProfile.hasActivePlan(userProfile)) {
      return openPaywallModal();
    }
    this.props.joinWorkout();
    navigation.navigate('HomeWorkouts', {
      booking,
      userProfile,
      workoutSessionService,
    });
  }

  shouldShowCheckinButton() {
    const { timeRemainingMilliseconds, isCheckedIn, enableStudioAppCheckIn } = this.state;
    const maxNumberOfMillisecsPriorToExerciseForCheckin =
      BookingsConfig.maxNumberOfMinutesPriorToExerciseForCheckin * 60 * 1000;
    const isTooEarlyForCheckin = maxNumberOfMillisecsPriorToExerciseForCheckin < timeRemainingMilliseconds;

    return !isCheckedIn && !isTooEarlyForCheckin && enableStudioAppCheckIn;
  }

  render() {
    const { booking, buttonContainer } = this.props;
    const { timeRemaining, bookingIsForHome, isWorkoutStartingInOneHour }
      = this.state;
    const hasFirstExerciseStarted = this.getFirstExerciseHasStarted();

    if (!isWorkoutStartingInOneHour || !booking) {
      return <></>;
    }

    if (bookingIsForHome) {
      return (
        <View style={buttonContainer}>
          {this.renderHomeButton(hasFirstExerciseStarted, timeRemaining)}
        </View>
      );
    } else {
      return (
        <View style={buttonContainer}>
          {this.renderStudioButton(hasFirstExerciseStarted)}
        </View>
      );
    }
  }

  renderHomeButton(hasFirstExerciseStarted) {
    const { t, buttonStyles } = this.props;
    const { timeRemaining, hasPreWorkoutStarted } = this.state;

    if (hasPreWorkoutStarted) {
      return <JoinOrCheckinWorkoutButton
        actionText={hasFirstExerciseStarted ? t('joinNow') : t('setUpNow')}
        t={t}
        onPress={this.openHomeWorkout}
        timeRemaining={timeRemaining}
        hasFirstExerciseStarted={hasFirstExerciseStarted}
        roundEdges={this.props.roundEdges}
        containerStyles={buttonStyles}/>;
    }

    return (
      <JoinOrCheckinWorkoutButton
        t={t}
        actionText={t('settings')}
        onPress={this.props.openBookingSettings}
        timeRemaining={timeRemaining}
        hasFirstExerciseStarted={hasFirstExerciseStarted}
        roundEdges={this.props.roundEdges}
        containerStyles={buttonStyles}
      />
    );
  }

  renderStudioButton(hasFirstExerciseStarted) {
    const { t, buttonStyles } = this.props;
    const { timeRemaining, pendingInviteAccept, shouldShowCheckin } = this.state;
    const gymForBooking = GymResolver.getByName(this.props.booking.gymName);
    if (pendingInviteAccept) {
      return (
        <JoinOrCheckinWorkoutButton
          t={t}
          actionText={t('acceptInviteShort')}
          onPress={this.props.openBookingSettings}
          timeRemaining={timeRemaining}
          hasFirstExerciseStarted={hasFirstExerciseStarted}
          roundEdges={this.props.roundEdges}
          containerStyles={buttonStyles}
        />
      );
    }

    if (shouldShowCheckin) {
      const isBalanceWorkout = this.props.booking?.settings.cardioOrResistance ===  CardioOrResistance.Balance;
      const firstCheckinStep = gymForBooking.bagContactAllowed && !isBalanceWorkout
        ? this.props.openBagContactStep
        : this.props.checkinToStudioWorkout;
      return (
        <JoinOrCheckinWorkoutButton
          t={t}
          actionText={t('checkIn')}
          onPress={firstCheckinStep}
          timeRemaining={timeRemaining}
          hasFirstExerciseStarted={hasFirstExerciseStarted}
          roundEdges={this.props.roundEdges}
          containerStyles={buttonStyles}
        />
      );
    }

    return (
        <JoinOrCheckinWorkoutButton
          t={t}
          actionText={t('settings')}
          onPress={this.props.openBookingSettings}
          timeRemaining={timeRemaining}
          hasFirstExerciseStarted={hasFirstExerciseStarted}
          roundEdges={this.props.roundEdges}
          containerStyles={buttonStyles}
        />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isBookingModalVisible: state.bookings.bookingWizard.isBookingModalVisible,
    workoutSession: state.bookings.bookingWizard.workoutSession,
    openInstantWorkout: state.auth.signup.openInstantWorkout,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { userProfile, booking } = ownProps;
  return {
    checkinToStudioWorkout: () => dispatch(checkinToStudioWorkout(userProfile, booking)),
    openBagContactStep: () => dispatch(openBagContactStep(userProfile, booking)),
    openBookingSettings: () => dispatch(openBookingSettings(userProfile, booking)),
    joinWorkout: () => dispatch(joinWorkout()),
    openPaywallModal: () => dispatch(openPaywallModalAction(PaywallPage.Workout)),
  };
};

WorkoutButtonWithCountdownAndActions = translate(['bookings'], { wait: true })
  (connect(mapStateToProps, mapDispatchToProps)(WorkoutButtonWithCountdownAndActions));
export default WorkoutButtonWithCountdownAndActions;
